import { motion } from "motion/react";
import React from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import NavigationIcon from "../../../image/icons/navigation/Navigation.svg";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";

interface ItineraryDetailsNavigateCtaProps {

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Whether turn-by-turn navigation is currently active for the selected itinerary. */
    "isFollowingNavigation": boolean;

    /** Starts or stops constrained navigation. */
    "onNavigate": () => void;
}

/**
 * Floating capsule CTA that starts or stops constrained navigation for the selected itinerary.
 *
 * @remarks Sole primary action below the chrome row now that saving lives as a lightweight
 * saved-itinerary toggle in the header; filled vs. tinted styling communicates the on/off state
 * without introducing colors outside {@link getUIColorPalette}.
 */
const ItineraryDetailsNavigateCta: React.FC<ItineraryDetailsNavigateCtaProps> = ( {
    isDarkUi,
    isFollowingNavigation,
    onNavigate,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    const buttonStyle: React.CSSProperties = {
        "borderColor":     palette.brandPrimary,
        "backgroundColor": isFollowingNavigation ? palette.brandPrimary : `${ palette.brandPrimary }14`,
        "color":           isFollowingNavigation ? palette.textOnBrandPrimary : palette.brandPrimary,
    };

    return (
        <div
            className="flex w-full justify-center px-2 pb-2"
            id="itinerary-details-navigate-cta"
        >
            <motion.button
                aria-label={isFollowingNavigation ? "Arrêter la navigation" : "Commencer la navigation"}
                className="inline-flex items-center gap-2 rounded-full border-2 border-solid px-4 py-2 text-xs font-bold shadow-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                onClick={onNavigate}
                style={buttonStyle}
                type="button"
                whileTap={animationsEnabled ? { "scale": 0.94 } : undefined}
            >
                <img
                    alt=""
                    aria-hidden={true}
                    className="h-4 w-4 shrink-0"
                    src={NavigationIcon}
                />
                {isFollowingNavigation ? "Arrêter la navigation" : "Commencer la navigation"}
            </motion.button>
        </div>
    );
};

export default ItineraryDetailsNavigateCta;
